/*
 * Product gallery carousel (snippets/cc-pdp-gallery.liquid).
 *
 * On mobile the gallery track is a horizontal scroll-snap strip, so swiping is native. This
 * script keeps the "n / N" counter and the dots in step with the scroll position, wires the
 * prev/next arrows and dots, and opens a full-screen zoom <dialog> for the current image.
 * On desktop the track is the tile mosaic and the controls are hidden by CSS.
 */
(() => {
  if (customElements.get('cc-pdp-gallery')) return;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class CcPdpGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-cc-gallery-track]');
      this.slides = Array.from(this.querySelectorAll('[data-cc-gallery-slide]'));
      if (!this.track || this.slides.length === 0) return;
      this.current = this.querySelector('[data-cc-gallery-current]');
      this.dots = Array.from(this.querySelectorAll('[data-cc-gallery-dot]'));
      this.dialog = this.querySelector('[data-cc-gallery-dialog]');
      this.index = 0;

      this.track.addEventListener('scroll', () => {
        window.cancelAnimationFrame(this.frame);
        this.frame = window.requestAnimationFrame(() => this.syncFromScroll());
      }, { passive: true });
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') { event.preventDefault(); this.go(this.index + 1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(this.index - 1); }
      });
      this.querySelectorAll('[data-cc-gallery-prev]').forEach((button) => {
        button.addEventListener('click', () => this.go(this.index - 1));
      });
      this.querySelectorAll('[data-cc-gallery-next]').forEach((button) => {
        button.addEventListener('click', () => this.go(this.index + 1));
      });
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.go(Number(dot.dataset.ccGalleryDot)));
      });

      const zoom = this.querySelector('[data-cc-gallery-zoom]');
      if (zoom && this.dialog && typeof this.dialog.showModal === 'function') {
        zoom.addEventListener('click', () => this.openZoom());
        this.querySelector('[data-cc-gallery-close]').addEventListener('click', () => this.dialog.close());
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        });
      } else if (zoom) {
        zoom.hidden = true;
      }
    }

    isCarousel() {
      return this.track.scrollWidth > this.track.clientWidth + 1;
    }

    syncFromScroll() {
      if (!this.isCarousel()) return;
      const width = this.track.clientWidth || 1;
      const index = Math.round(Math.abs(this.track.scrollLeft) / width);
      this.setIndex(Math.max(0, Math.min(this.slides.length - 1, index)));
    }

    setIndex(index) {
      this.index = index;
      if (this.current) this.current.textContent = String(index + 1);
      this.dots.forEach((dot, i) => dot.setAttribute('aria-pressed', i === index ? 'true' : 'false'));
      if (this.dialog && this.dialog.open) this.showInZoom(index);
    }

    go(index) {
      const count = this.slides.length;
      const next = ((index % count) + count) % count;
      if (this.isCarousel()) {
        this.track.scrollTo({ left: next * this.track.clientWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
      }
      this.setIndex(next);
    }

    showInZoom(index) {
      const source = this.slides[index] && this.slides[index].querySelector('.cc-gallery__img');
      if (!source || !this.dialog) return;
      if (!this.zoomImg) {
        // Built on first use, so the page carries no empty <img> for the zoom view.
        this.zoomImg = document.createElement('img');
        this.zoomImg.className = 'cc-gallery-zoom__img';
        this.dialog.prepend(this.zoomImg);
      }
      this.zoomImg.removeAttribute('srcset');
      if (source.getAttribute('srcset')) {
        this.zoomImg.setAttribute('srcset', source.getAttribute('srcset'));
        this.zoomImg.setAttribute('sizes', '100vw');
      }
      this.zoomImg.src = source.currentSrc || source.src;
      this.zoomImg.alt = source.alt || '';
    }

    openZoom() {
      this.showInZoom(this.index);
      this.dialog.showModal();
    }
  }

  customElements.define('cc-pdp-gallery', CcPdpGallery);
})();
